import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import {
    IconSearch,
    IconCalendar,
    IconDownload,
    IconTrash,
    IconPrinter,
    IconFileText,
    IconPhoto,
    IconPlus,
    IconShare,
    IconCopy,
    IconEye,
    IconPencil,
    IconCheck,
    IconX,
    IconRefresh,
    IconRepeat,
    IconCaretRight,
    IconCaretDown,
    IconLayoutGrid,
    IconList,
    IconSparkles,
    IconDots,
    IconChevronLeft,
    IconChevronRight,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useDebouncedValue, useMediaQuery } from '@/lib/hooks';
import { ModuleGlyph, MY_DOCS_ICON_COLOR } from '../../../constants/moduleColors';
import { shareImageToTarget, copyImageToClipboard } from '../../../utils/imageShare';
import { markPrivateResultDelivered } from '../../../utils/privateResults';
import GenerateDocDrawer from '../../../components/user/GenerateDocDrawer';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import PaginationBar from '@/components/common/PaginationBar';
import generatedDocService from '../../../services/generatedDocService';
import templateSetService from '../../../services/templateSetService';
import tagService from '../../../services/tagService';
import categoryService from '../../../services/categoryService';
import imageGenerationService from '../../../services/imageGenerationService';
import aiTemplateService from '../../../services/aiTemplateService';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import PrintMediaTab from './PrintMediaTab';
import QuestionsTab from './QuestionsTab';
import BulkPhotoEditList from '../bulk-photo-edit/BulkPhotoEditList';
import AiDocumentsList from '../ai-documents/AiDocumentsList';

import CustomDownloadModal from '../generate-image/CustomDownloadModal';
import PrintModal from '../generate-image/PrintModal';
import { isSpecialBgToken, specialBgCss, hasTransparentSubject, compositeBackground } from '../generate-image/bgPresets';
import ImagePreviewModal from '../../../components/common/ImagePreviewModal';
import SelectField from '../../../components/common/SelectField';
import DatePickerField from '../../../components/common/DatePickerField';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import TipBtn from '../../../components/common/TipBtn';
import { generateDocPdf, downloadBlob, printBlob, shareBlobs } from '../../../utils/generateDocPdf';
import QualityBadge from '../../../components/common/QualityBadge';

// The date picker returns a single day; filter that WHOLE day (00:00 → 23:59.999).
// Sending only a start date makes the backend query `createdAt >= start`, which returns
// everything from that day ONWARD (so a past date looks like "no filter applied").
const toDayRange = (date) => {
    if (!date) return [null, null];
    const start = new Date(date); start.setHours(0, 0, 0, 0);
    const end   = new Date(date); end.setHours(23, 59, 59, 999);
    return [start, end];
};

// AI-photo-edit filter helpers: map known background tokens to an i18n key suffix
// (imageFilters.color_<suffix>) and prettify free-form dressCode keys for display.
const BG_COLOR_KEY = {
    '#ffffff': 'white', white: 'white',
    '#2196F3': 'blue', blue: 'blue',
    '#03A9F4': 'skyBlue', sky_blue: 'skyBlue',
    '#9E9E9E': 'grey', grey: 'grey',
    '#3068E1': 'darkBlue', dark_blue: 'darkBlue',
    '#00897B': 'teal', teal: 'teal',
    '#FFF8DC': 'offWhite', off_white: 'offWhite',
};
const prettifyDress = (key) => String(key || '')
    .replace(/\|/g, ' | ')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Bangla labels for the dress-code filter. Dress codes are `{colour}_{garment}` keys
// (dual variants joined by `|`), so we translate each part and fall back to prettified
// English for anything not mapped.
const DRESS_COLOR_BN = {
    black: 'কালো', blue: 'নীল', red: 'লাল', white: 'সাদা', olive: 'অলিভ', orange: 'কমলা',
    brown: 'বাদামি', gold: 'সোনালি', gray: 'ধূসর', grey: 'ধূসর', green: 'সবুজ', pink: 'গোলাপি',
    sky: 'আকাশি', navy: 'নেভি ব্লু', maroon: 'মেরুন', cream: 'ক্রিম', purple: 'বেগুনি',
    yellow: 'হলুদ', teal: 'টিল', rose: 'রোজ', casual: 'ক্যাজুয়াল', couple: 'কাপল', charcoal: 'চারকোল',
};
const DRESS_GARMENT_BN = {
    suit: 'স্যুট', suite: 'স্যুট', 'suite-tieless': 'টাইবিহীন স্যুট', 'suit-tieless': 'টাইবিহীন স্যুট',
    shirt: 'শার্ট', 't-shirt': 'টি-শার্ট', 'round-collar-tshirt': 'রাউন্ড কলার টি-শার্ট',
    hijab: 'হিজাব', hizab: 'হিজাব', 'kameez-hijab': 'কামিজ-হিজাব', 'orna-kameez': 'ওড়না-কামিজ',
    'shari-hijab': 'শাড়ি-হিজাব', sharee: 'শাড়ি', shari: 'শাড়ি', 'sharee-design': 'শাড়ি',
    saree: 'শাড়ি', panjabi: 'পাঞ্জাবি', panjbi: 'পাঞ্জাবি', jacket: 'জ্যাকেট', tops: 'টপস',
    abaya: 'আবায়া', thobe: 'থোব', 'thobe-ghutra': 'থোব + গুত্রা', 'nehru-jacket': 'হাতাবিহীন জ্যাকেট', 'hijab-formal': 'হিজাব ফরমাল',
    frock: 'ফ্রক', blouse: 'ব্লাউজ', kameez: 'কামিজ', orna: 'ওড়না',
};
const DRESS_SPECIAL_BN = { original: 'অপরিবর্তিত', away: 'অন্যান্য' };
const translateDressPart = (part, isBn) => {
    if (!part) return '';
    if (!isBn) return prettifyDress(part);
    if (DRESS_SPECIAL_BN[part]) return DRESS_SPECIAL_BN[part];
    const idx = part.indexOf('_');
    if (idx === -1) return DRESS_GARMENT_BN[part] || DRESS_COLOR_BN[part] || prettifyDress(part);
    const color = part.slice(0, idx);
    const garment = part.slice(idx + 1);
    return `${DRESS_COLOR_BN[color] || prettifyDress(color)} ${DRESS_GARMENT_BN[garment] || prettifyDress(garment)}`;
};
const translateDress = (key, isBn) => String(key || '').split('|').map((p) => translateDressPart(p, isBn)).join(' | ');

// ─── Manual-edit background colour resolver ───────────────────────────────────
const MANUAL_BG_HEX = {
    white:     '#ffffff',
    blue:      '#2196F3',
    sky_blue:  '#03A9F4',
    grey:      '#9E9E9E',
    dark_blue: '#3068E1',
    teal:      '#00897B',
    off_white: '#FFF8DC',
};
const resolveManualBg = (bg) => {
    if (!bg || bg === 'transparent') return 'transparent';
    if (bg.startsWith('#') || bg.startsWith('rgb')) return bg;
    return MANUAL_BG_HEX[bg] || bg;
};
// Solid colour for a filter swatch (null for the transparent token → checkerboard chip).
const bgSwatchColor = (token) => {
    if (!token || token === 'transparent') return null;
    if (MANUAL_BG_HEX[token]) return MANUAL_BG_HEX[token];
    if (String(token).startsWith('#') || String(token).startsWith('rgb')) return token;
    return '#e5e7eb';
};
// Checkerboard shown behind a genuinely transparent (no-colour) image so it reads as
// transparent instead of picking up the page's grey behind a `transparent` card.
const CHECKERBOARD = 'repeating-conic-gradient(#cbd5e1 0% 25%,#fff 0% 50%) 0 0/16px 16px';
// Style object for a card background — handles gradient:/pattern:/preset: tokens too.
const manualBgStyle = (bg) => {
    if (!bg || bg === 'transparent') return { background: CHECKERBOARD };
    if (isSpecialBgToken(bg)) return specialBgCss(bg) || { backgroundColor: 'transparent' };
    return { backgroundColor: resolveManualBg(bg) };
};

// Pinterest-style masonry columns (uniform width, height follows each image).
const imageMasonryBreakpoints = { default: 5, 1280: 4, 1024: 3, 640: 2 };

// Staggered placeholder heights so the loading skeleton mimics the varied
// aspect ratios of the real masonry grid (cycled by index).
const IMAGE_SKELETON_HEIGHTS = [300, 380, 240, 340, 280, 420, 260, 360, 320, 220];

// ─── Masonry grid (for AI Template Generated sub-tab) ────────────────────────
const GRID_ROW_HEIGHT = 10;
const GRID_GAP = 12;

function MasonryItem({ children, isLandscape }) {
    const ref = useRef(null);
    const [rowSpan, setRowSpan] = useState(20);

    useLayoutEffect(() => {
        if (!ref.current) return;
        const el = ref.current.firstChild;
        const compute = () => {
            if (!el) return;
            const h = el.getBoundingClientRect().height;
            const span = Math.ceil((h + GRID_GAP) / (GRID_ROW_HEIGHT + GRID_GAP));
            setRowSpan(span);
        };
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{
                gridRowEnd: `span ${rowSpan}`,
                gridColumn: isLandscape ? 'span 2' : undefined,
            }}
        >
            {children}
        </div>
    );
}

function MasonryGrid({ children }) {
    return (
        <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
            style={{ gridAutoRows: `${GRID_ROW_HEIGHT}px`, gap: `${GRID_GAP}px` }}
        >
            {children}
        </div>
    );
}

// ─── Tiny helper components ───────────────────────────────────────────────────

function Badge({ children, color = 'blue', variant = 'light', size = 'sm' }) {
    const colorMap = {
        blue:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        green:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
        red:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        grape:  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        gray:   'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
    };
    const cls = colorMap[color] || colorMap.blue;
    return (
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-medium ${size === 'xs' ? 'text-[10px]' : 'text-xs'} ${cls}`}>
            {children}
        </span>
    );
}

// Minimal dropdown menu
function DropdownMenu({ trigger, items }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const computePos = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const menuW = 176;
        const menuH = 340; // approx menu height
        let left = rect.right - menuW;
        if (left < 4) left = rect.left;
        // Prefer opening above the trigger (over the card image); flip below only
        // when there isn't enough room above and there's more room below.
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        const above = spaceAbove >= menuH || spaceAbove >= spaceBelow;
        const top = above ? rect.top - 4 : rect.bottom + 4;
        setPos({ top, left, above });
    };

    const openMenu = () => {
        computePos();
        setOpen(o => !o);
    };

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        // Keep the fixed-position menu glued to its trigger while scrolling/resizing.
        // `capture: true` catches scrolls inside any ancestor container too.
        const reposition = () => computePos();
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open]);

    return (
        <div style={{ position: 'relative', display: 'inline-flex' }}>
            <div ref={triggerRef} onClick={openMenu}>{trigger}</div>
            {open && (
                <div
                    ref={menuRef}
                    style={{
                        position: 'fixed',
                        top: pos.above ? 'auto' : pos.top,
                        bottom: pos.above ? window.innerHeight - pos.top : 'auto',
                        left: pos.left,
                        width: 176,
                        zIndex: 9999,
                    }}
                    className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg py-1"
                    onClick={() => setOpen(false)}
                >
                    {items.map((item, i) => (
                        item.label ? (
                            <p key={i} className="px-3 py-1 text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">{item.label}</p>
                        ) : (
                            <button key={i} onClick={item.onClick} className="w-full flex items-center gap-2 px-3 py-1 text-[11px] text-left text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
                                {item.icon && <span className="w-4">{item.icon}</span>}
                                {item.text}
                            </button>
                        )
                    ))}
                </div>
            )}
        </div>
    );
}

// PaginationBar + pageWindow now live in components/common/PaginationBar.jsx (shared
// across the app so every table uses the same pagination design).

export default function GeneratedDocuments() {
    const { setId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const { user } = useAuthStore();
    // Template documents and question papers are Bangladesh-only features (unset country → still
    // shown). Everyone gets the "Documents" tab, but non-BD users only see its AI-document list.
    const isBdUser = !user?.country || user.country === 'BD';
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Tab state - initialize from URL query param. Non-BD users skip the question-papers tab.
    const [activeTab, setActiveTab] = useState(() => {
        const tabFromUrl = searchParams.get('tab');
        const allowed = ['documents', 'printMedia', 'images', 'questions'].filter((tb) => isBdUser || tb !== 'questions');
        if (tabFromUrl && allowed.includes(tabFromUrl)) return tabFromUrl;
        return 'images';
    });

    const [loading, setLoading] = useState(true);
    const [imagesLoading, setImagesLoading] = useState(true);
    const [docSets, setDocSets] = useState([]);
    const [generatedImages, setGeneratedImages] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    // Monotonic id for the latest documents request; stale async responses are ignored
    // (prevents an earlier/slower fetch from clobbering a newer page's data & page count).
    const docReqIdRef = useRef(0);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [dateRange, setDateRange] = useState([null, null]);
    const [categoryIdFilter, setCategoryIdFilter] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [docSortOrder, setDocSortOrder] = useState('newest');

    // Filter options
    const [categories, setCategories] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);

    // Image filters
    const [imageSearch, setImageSearch] = useState('');
    const [debouncedImageSearch] = useDebouncedValue(imageSearch, 300);
    const [imageDateRange, setImageDateRange] = useState([null, null]);
    const [imageSortOrder, setImageSortOrder] = useState('newest');
    // AI-photo-edit only: filter by stored background colour / dress code. Options are the
    // distinct values that actually exist in the user's AI photos (fetched on demand).
    const [imageBgFilter, setImageBgFilter] = useState('');
    const [imageDressFilter, setImageDressFilter] = useState('');
    const [imageCategoryFilter, setImageCategoryFilter] = useState(''); // AI-template sub-tab only
    const [imageFilterOptions, setImageFilterOptions] = useState({ backgrounds: [], dressCodes: [], categories: [] });
    const [imageSubTab, setImageSubTab] = useState(() => {
        const sub = searchParams.get('sub');
        return ['all', 'manual', 'aiTemplate', 'studio', 'bulk'].includes(sub) ? sub : 'all';
    }); // 'all' | 'manual' | 'aiTemplate' | 'studio' | 'bulk'
    // Documents tab sub-tabs: template-based ('general') vs AI-drafted ('ai').
    // Non-BD users have no template documents, so their Documents tab is the AI list alone.
    const [docSubTab, setDocSubTab] = useState(() => (!isBdUser || searchParams.get('sub') === 'ai' ? 'ai' : 'general'));
    const [imagePage, setImagePage] = useState(1);
    const [imageTotal, setImageTotal] = useState(0);
    const [imageHasMore, setImageHasMore] = useState(false); // infinite scroll: more pages available
    const [imageLoadingMore, setImageLoadingMore] = useState(false); // appending next page
    const loadMoreRef = useRef(null); // sentinel observed to auto-load the next page
    // Monotonic id for the latest image request; stale async responses are ignored (prevents shuffling races)
    const imageReqIdRef = useRef(0);
    const [hoveredImageId, setHoveredImageId] = useState(null);
    const [dualImageIds, setDualImageIds] = useState(new Set());
    const [imageViewMode, setImageViewMode] = useState(
        () => localStorage.getItem('imageViewMode') || 'grid'
    );
    const [docViewMode, setDocViewMode] = useState(
        () => localStorage.getItem('docViewMode') || (window.innerWidth < 768 ? 'grid' : 'list')
    );
    // Custom download modal state
    const [showCustomDownload, setShowCustomDownload] = useState(false);
    const [customDownloadImageUrl, setCustomDownloadImageUrl] = useState(null);
    const [customDownloadImageId, setCustomDownloadImageId] = useState(null);
    const [customDownloadImageBg, setCustomDownloadImageBg] = useState(null);

    // Print modal state
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printImageUrl, setPrintImageUrl] = useState(null);

    // Image name edit state
    const [editingImageId, setEditingImageId] = useState(null);
    const [editingImageName, setEditingImageName] = useState('');

    // Image preview modal state
    const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
    const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);

    // Image delete confirmation state
    const [showImageDeleteConfirm, setShowImageDeleteConfirm] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deletingImageId, setDeletingImageId] = useState(null);

    // Language options
    const languageOptions = [
        { value: '', label: t('common.allLanguages') },
        { value: 'bangla', label: t('templateSets.bangla') },
        { value: 'english', label: t('templateSets.english') },
        { value: 'mixed', label: t('templateSets.mixed') }
    ];

    // Delete confirmation
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);

    // Regenerate drawer
    const [regenerateDrawerOpened, setRegenerateDrawerOpened] = useState(false);
    const [groupToRegenerate, setGroupToRegenerate] = useState(null);

    // Fetch documents
    const loadDocuments = useCallback(async () => {
        const reqId = ++docReqIdRef.current; // tag this request; only the newest may commit results
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(debouncedSearch && { search: debouncedSearch }),
                ...(dateRange[0] && { startDate: dateRange[0].toISOString() }),
                ...(dateRange[1] && { endDate: dateRange[1].toISOString() }),
                ...(categoryIdFilter && { categoryId: parseInt(categoryIdFilter) }),
                ...(languageFilter && { language: languageFilter }),
                ...(docSortOrder && { sortOrder: docSortOrder }),
                ...(selectedTags.length > 0 && { tagIds: selectedTags.join(',') }),
                ...(selectedOfficeSpace && { officeSpaceId: selectedOfficeSpace.id })
            };

            const response = await generatedDocService.getGeneratedDocs(params);
            if (reqId !== docReqIdRef.current) return; // stale response — don't clobber newer data
            if (response.success) {
                setDocSets(response.data || []);
                // Merge only the server-derived counts. Never overwrite page/limit from the
                // response: those are the client-driven values this callback depends on, and
                // replacing them can retrigger the effect (loop) or apply a stale page count.
                setPagination(prev => {
                    const total = response.pagination?.total ?? prev.total;
                    // Derive the page count from `total` and OUR own `limit` instead of trusting
                    // the backend's `totalPages`: the API can report an inconsistent value
                    // (e.g. total=34, limit=10, but totalPages=8), which produced phantom pages
                    // that rendered stale/empty data and desynced the active-page indicator.
                    const totalPages = total > 0 ? Math.ceil(total / prev.limit) : 0;
                    return { ...prev, total, totalPages };
                });
            }
        } catch (error) {
            console.error('Load error:', error);
            notifications.show({ title: t('common.error'), message: t('documents.failedToLoad'), color: 'red' });
        } finally {
            if (reqId === docReqIdRef.current) setLoading(false);
        }
    }, [pagination.page, pagination.limit, debouncedSearch, dateRange, categoryIdFilter, languageFilter, selectedTags, docSortOrder, selectedOfficeSpace]);

    const loadTags = async () => {
        try {
            const response = await tagService.getAllTags();
            setAvailableTags((response.data || []).map(tag => ({ value: tag.id.toString(), label: tag.name })));
        } catch (error) {
            console.error('Failed to load tags:', error);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categoryService.getAllCategories();
            const categoryData = response.data || [];
            setCategories([
                { value: '', label: t('common.allCategories') },
                ...categoryData.map(category => ({ value: category.id.toString(), label: category.name }))
            ]);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    // Each sub-tab maps to a single backend source (images are now stored in
    // separate tables), so every tab is a plain server-paginated query. 'all' =
    // regular AI photos.
    const sourceFilterMap = { all: 'regular', manual: 'manual', aiTemplate: 'ai-templates', studio: 'studio' };
    const IMAGE_PAGE_SIZE = 20;

    // Infinite scroll: page 1 replaces the list; later pages append. `append` is
    // set by the IntersectionObserver as the sentinel scrolls into view.
    const loadImages = useCallback(async (page = 1, append = false) => {
        const reqId = ++imageReqIdRef.current; // tag this request; only the newest may commit results
        try {
            if (append) setImageLoadingMore(true); else setImagesLoading(true);
            const response = await imageGenerationService.getHistory({
                page,
                limit: IMAGE_PAGE_SIZE,
                ...(debouncedImageSearch && { search: debouncedImageSearch }),
                ...(imageDateRange[0] && { startDate: imageDateRange[0].toISOString() }),
                ...(imageDateRange[1] && { endDate: imageDateRange[1].toISOString() }),
                ...(imageSortOrder && { sortOrder: imageSortOrder }),
                ...(selectedOfficeSpace && { officeSpaceId: selectedOfficeSpace.id }),
                ...(sourceFilterMap[imageSubTab] ? { source: sourceFilterMap[imageSubTab] } : {}),
                // Background filter → AI-photo-edit ('all') + Manual + Studio; dress → 'all' + Studio;
                // category → AI-template ('aiTemplate') only.
                ...((imageSubTab === 'all' || imageSubTab === 'manual' || imageSubTab === 'studio') && imageBgFilter && { background: imageBgFilter }),
                ...((imageSubTab === 'all' || imageSubTab === 'studio') && imageDressFilter && { dressCode: imageDressFilter }),
                ...(imageSubTab === 'aiTemplate' && imageCategoryFilter && { category: imageCategoryFilter }),
            });
            if (reqId !== imageReqIdRef.current) return; // stale response — don't clobber newer data
            if (response.success && response.data) {
                const history = response.data.history || [];
                const total = response.data.pagination?.total ?? history.length;
                setGeneratedImages(prev => (append ? [...prev, ...history] : history));
                setImagePage(page);
                setImageTotal(total);
                setImageHasMore(page < Math.max(1, Math.ceil(total / IMAGE_PAGE_SIZE)));
                setDualImageIds(new Set());
            }
        } catch (error) {
            console.error('Load images error:', error);
            notifications.show({ title: t('common.error'), message: 'Failed to load generated images', color: 'red' });
        } finally {
            if (reqId === imageReqIdRef.current) { setImagesLoading(false); setImageLoadingMore(false); }
        }
    }, [debouncedImageSearch, imageDateRange, imageSortOrder, selectedOfficeSpace, imageSubTab, imageBgFilter, imageDressFilter, imageCategoryFilter]); // eslint-disable-line

    // All tabs are server-filtered now — no client-side exclusion/paging.
    const displayedImages = generatedImages;
    const pagedImages = generatedImages;

    // Common "reset all filters" for the images tab (search, date, bg, dress, sort).
    const hasActiveImageFilters = !!imageSearch || !!imageDateRange[0] || !!imageBgFilter || !!imageDressFilter || !!imageCategoryFilter || imageSortOrder !== 'newest';
    const resetImageFilters = () => {
        setImageSearch('');
        setImageDateRange([null, null]);
        setImageBgFilter('');
        setImageDressFilter('');
        setImageCategoryFilter('');
        setImageSortOrder('newest');
    };

    // Auto-load the next page when the sentinel scrolls near the viewport.
    useEffect(() => {
        if (activeTab !== 'images') return undefined;
        const el = loadMoreRef.current;
        if (!el) return undefined;
        const obs = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && imageHasMore && !imagesLoading && !imageLoadingMore) {
                loadImages(imagePage + 1, true);
            }
        }, { rootMargin: '600px' });
        obs.observe(el);
        return () => obs.disconnect();
    }, [activeTab, imageHasMore, imagesLoading, imageLoadingMore, imagePage, loadImages]);

    // Switching image sub-tab: clear the previous sub-tab's images and flag loading
    // in one update so the skeleton shows immediately (no stale content / no "no
    // images" flash) until the new sub-tab's data arrives.
    const changeImageSubTab = (key) => {
        if (key === imageSubTab) return;
        setGeneratedImages([]);
        setImagesLoading(true);
        setImagePage(1);
        // Each sub-tab supports different filters — clear the ones the destination doesn't
        // support so a stale filter doesn't silently constrain its request.
        // Dress → 'all'+'studio'; background → 'all'+'manual'+'studio'; category → 'aiTemplate' only.
        if (key !== 'all' && key !== 'studio') setImageDressFilter('');
        if (key !== 'all' && key !== 'manual' && key !== 'studio') setImageBgFilter('');
        if (key !== 'aiTemplate') setImageCategoryFilter('');
        setImageSubTab(key);
    };

    const changeDocSubTab = (key) => {
        if (key === docSubTab) return;
        setDocSubTab(key);
        setSearchParams({ tab: 'documents', sub: key }, { replace: true });
    };

    // Fetch the filter options for the sub-tabs that support them: 'all' (bg + dress),
    // 'manual' (bg), 'aiTemplate' (category). Scoped to the sub-tab's source.
    useEffect(() => {
        if (activeTab !== 'images' || !['all', 'manual', 'aiTemplate', 'studio'].includes(imageSubTab)) {
            setImageFilterOptions({ backgrounds: [], dressCodes: [], categories: [] });
            return;
        }
        const src = sourceFilterMap[imageSubTab];
        imageGenerationService.getFilterOptions({
            ...(selectedOfficeSpace ? { officeSpaceId: selectedOfficeSpace.id } : {}),
            ...(src ? { source: src } : {}),
        })
            .then((res) => setImageFilterOptions({
                backgrounds: res?.data?.backgrounds || [],
                dressCodes: res?.data?.dressCodes || [],
                categories: res?.data?.categories || [],
            }))
            .catch(() => setImageFilterOptions({ backgrounds: [], dressCodes: [], categories: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, imageSubTab, selectedOfficeSpace]);

    const handleTabChange = (newTab) => {
        if (newTab === activeTab) return;
        setSearchParams({ tab: newTab }, { replace: true });
        if (newTab === 'documents') setDocSets([]);
        else if (newTab === 'images') { setGeneratedImages([]); setImagesLoading(true); }
        if (newTab === 'documents') setLoading(true);
        setPagination(prev => ({ ...prev, page: 1 }));
        setActiveTab(newTab);
    };

    useEffect(() => {
        if (activeTab === 'documents' && isBdUser) loadDocuments(); // template documents are BD-only
        // images are loaded solely by the filter/sub-tab/activeTab effect below to avoid a
        // duplicate concurrent fetch (loadImages identity changes on the same triggers).
        loadTags();
        loadCategories();
    }, [loadDocuments, activeTab, isBdUser]);

    useEffect(() => {
        const loadGroupForRegenerate = async () => {
            const groupId = searchParams.get('groupId') || setId;
            if (!groupId || groupToRegenerate) return;
            try {
                setLoading(true);
                const groupResponse = await generatedDocService.getDocSetById(groupId);
                if (!groupResponse.success) throw new Error('Failed to load document group');
                const docSet = groupResponse.data;
                const templateSetResponse = await templateSetService.getTemplateSetById(docSet.templateSetId);
                if (templateSetResponse.data) docSet.templateSet = templateSetResponse.data;
                if (!docSet.templateSet?.placeholders) {
                    notifications.show({ title: t('common.error'), message: t('documents.placeholdersNotLoaded'), color: 'red' });
                    navigate('/documents');
                    return;
                }
                // Replace URL with the template set slug now that we have it
                const slug = docSet.templateSet?.slug;
                if (slug && setId !== slug) {
                    navigate(`/documents/regenerate/${slug}?groupId=${groupId}`, { replace: true });
                }
                setGroupToRegenerate(docSet);
                setRegenerateDrawerOpened(true);
            } catch (error) {
                console.error('Load group error:', error);
                notifications.show({ title: t('common.error'), message: t('documents.failedToLoadDetails') || error.message, color: 'red' });
                navigate('/documents');
            } finally {
                setLoading(false);
            }
        };
        loadGroupForRegenerate();
    }, [setId, searchParams, navigate]);

    useEffect(() => {
        if (pagination.page !== 1) setPagination(prev => ({ ...prev, page: 1 }));
    }, [debouncedSearch, dateRange, categoryIdFilter, languageFilter, selectedTags, docSortOrder]);

    // Keep the current page within range once the counts update (e.g. a delete or a filter
    // shrinks the result set). Converges in one step: snapping to the last real page triggers
    // a refetch whose count leaves page in range, so it settles without looping.
    useEffect(() => {
        if (pagination.totalPages > 0 && pagination.page > pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: prev.totalPages }));
        }
    }, [pagination.totalPages, pagination.page]);

    // Single source of image loading: reset and reload from page 1 whenever the images tab
    // becomes active or its filters/sub-tab change. Sole caller of loadImages for the tab.
    useEffect(() => {
        // The 'bulk' sub-tab renders the Bulk Photo Edit list, which loads its own
        // data — skip the image-history fetch there.
        if (activeTab === 'images' && imageSubTab !== 'bulk') {
            setGeneratedImages([]);
            setImagePage(1);
            loadImages(1);
        }
        // selectedOfficeSpace is only re-resolved (from null) after fetchOfficeSpaces()
        // completes, so it must be a dep — otherwise the images tab loads once with a
        // stale/null shop and never refetches when the shop resolves or the user switches.
    }, [activeTab, debouncedImageSearch, imageDateRange, imageSortOrder, imageSubTab, imageBgFilter, imageDressFilter, imageCategoryFilter, selectedOfficeSpace]); // eslint-disable-line

    const goToDocPage = (p) => {
        if (p < 1 || p > pagination.totalPages || p === pagination.page) return;
        setPagination(prev => ({ ...prev, page: p }));
        window.scrollTo({ top: 0, behavior: 'smooth' }); // scroll to top on page change, like images
    };

    const toggleGroup = (gId) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(gId)) newSet.delete(gId);
            else newSet.add(gId);
            return newSet;
        });
    };

    const handleDownloadPDF = async (doc, group) => {
        const notifId = 'pdf-download';
        const templateDocs = group?.templateSet?.docs || [];
        const docIdx = (group?.generatedDocs || []).findIndex(d => d.id === doc.id);
        const templateDoc = templateDocs[docIdx] ?? templateDocs[0];
        if (!templateDoc?.contentKey) {
            notifications.show({ title: t('common.error'), message: t('documents.fileNotAvailable'), color: 'red' });
            return;
        }
        notifications.show({ id: notifId, title: t('documents.downloadingPdf'), message: t('documents.downloadingMessage'), color: 'blue', loading: true, autoClose: false });
        try {
            const blob = await generateDocPdf(templateDoc, group?.templateSet?.placeholders || [], group?.placeholderValues || {});
            downloadBlob(blob, `${doc.title || group?.title || 'document'}.pdf`);
            notifications.update({ id: notifId, title: t('common.success'), message: t('documents.pdfDownloaded'), color: 'green', loading: false, autoClose: 3000 });
        } catch (error) {
            console.error('PDF download error:', error);
            notifications.update({ id: notifId, title: t('common.error'), message: error.message, color: 'red', loading: false, autoClose: 3000 });
        }
    };

    const handleSaveImageName = async (imgId) => {
        if (!editingImageName.trim()) { setEditingImageId(null); return; }
        try {
            await imageGenerationService.updateImageName(imgId, editingImageName.trim());
            setGeneratedImages(prev => prev.map(img => img.id === imgId ? { ...img, fileName: editingImageName.trim() } : img));
            notifications.show({ title: t('common.success'), message: t('imageGeneration.nameUpdated') || 'Name updated successfully', color: 'green' });
        } catch (error) {
            console.error('Failed to update image name:', error);
            notifications.show({ title: t('common.error'), message: t('imageGeneration.nameUpdateFailed') || 'Failed to update name', color: 'red' });
        } finally {
            setEditingImageId(null);
            setEditingImageName('');
        }
    };

    const handleReopenImage = async (img) => {
        if (img.manuallyEdited === true) {
            // Pass full history item so the manual editor can restore all metadata
            navigate('/generate-image-manual', {
                state: {
                    historyItem: {
                        id: img.id,
                        aiImage: img.aiImage,
                        background: img.background,
                        size: img.size || img.photoSize,
                        zoom: img.zoom ?? null,
                        offsetX: img.offsetX ?? null,
                        offsetY: img.offsetY ?? null,
                        manualBgRemoved: img.manualBgRemoved ?? false,
                    },
                },
            });
        } else if (img.studioEdited === true) {
            // A Studio Editor photo reopens in the Studio Editor with every setting of the edit
            navigate(`/studio-editor?editId=${img.id}`);
        } else if (img.aiTemplateGeneration === true) {
            // Use stored slug if available; otherwise look up by template ID
            const slug = img.aiTemplateSlug;
            const id   = img.aiTemplateId;
            if (slug) {
                navigate(`/ai-templates/${slug}`);
            } else if (id) {
                try {
                    const res = await aiTemplateService.get(id);
                    const fetchedSlug = res?.data?.slug || res?.slug;
                    navigate(fetchedSlug ? `/ai-templates/${fetchedSlug}` : '/ai-templates');
                } catch {
                    navigate('/ai-templates');
                }
            } else {
                navigate('/ai-templates');
            }
        } else {
            navigate(`/generate-image?editId=${img.id}`);
        }
    };

    const handleDownloadImage = async (img) => {
        try {
            const raw = await imageGenerationService.downloadImage(img.id);
            // Composite the stored background for transparent-subject images (AI cut-outs
            // + manual edits) so the download matches the preview.
            const blob = hasTransparentSubject(img) ? await compositeBackground(raw, img.background) : raw;
            const uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10);
            const ext = blob.type === 'image/png' ? 'png' : 'jpg';
            const downloadName = `sohozkaj-image-${uuid}+${Date.now()}.${ext}`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = downloadName;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            notifications.show({ title: t('common.success'), message: t('imageGeneration.imageDownloaded') || 'ছবি ডাউনলোড হয়েছে', color: 'green' });
            imageGenerationService.trackDownload(img.id).catch(err => console.error('Failed to track download:', err));
        } catch (error) {
            console.error('Download error:', error);
            notifications.show({ title: t('common.error'), message: t('imageGeneration.downloadFailed') || 'ডাউনলোড ব্যর্থ', color: 'red' });
        }
    };

    const handlePrintDoc = async (doc, group) => {
        const notifId = 'pdf-print';
        const templateDocs = group?.templateSet?.docs || [];
        const docIdx = (group?.generatedDocs || []).findIndex(d => d.id === doc.id);
        const templateDoc = templateDocs[docIdx] ?? templateDocs[0];
        if (!templateDoc?.contentKey) {
            notifications.show({ title: t('common.error'), message: t('documents.fileNotAvailableForPrint'), color: 'red' });
            return;
        }
        notifications.show({ id: notifId, title: t('documents.preparingPdf'), message: t('documents.convertingForPrint'), color: 'blue', loading: true, autoClose: false });
        try {
            const blob = await generateDocPdf(templateDoc, group?.templateSet?.placeholders || [], group?.placeholderValues || {});
            const opened = printBlob(blob);
            if (!opened) {
                notifications.update({ id: notifId, title: t('common.error'), message: t('documents.allowPopups'), color: 'red', loading: false, autoClose: 3000 });
                return;
            }
            notifications.update({ id: notifId, title: t('common.success'), message: t('documents.pdfOpenedForPrint'), color: 'green', loading: false, autoClose: 2000 });
        } catch (error) {
            console.error('Print error:', error);
            notifications.update({ id: notifId, title: t('common.error'), message: error.message, color: 'red', loading: false, autoClose: 3000 });
        }
    };

    const handleRegenerateGroup = async (group) => {
        // Navigate with templateSetId in path + groupId as query param.
        // loadGroupForRegenerate will replace the URL with the real slug once loaded.
        navigate(`/documents/regenerate/${group.templateSetId}?groupId=${group.id}`);
    };

    const handleDrawerClose = () => {
        setRegenerateDrawerOpened(false);
        setGroupToRegenerate(null);
        navigate('/documents');
    };

    const handleRegenerateSuccess = () => {
        setRegenerateDrawerOpened(false);
        setGroupToRegenerate(null);
        navigate('/documents');
        loadDocuments();
        notifications.show({ title: t('common.success'), message: t('documents.regeneratedSuccessfully'), color: 'green' });
    };

    const handleDeleteGroup = (group) => {
        setGroupToDelete(group);
        setDeleteModalOpened(true);
    };

    const handleShareDocGroup = async (group) => {
        const notifId = 'doc-share';
        const templateDocs = group?.templateSet?.docs || [];
        const placeholders = group?.templateSet?.placeholders || [];
        const fieldValues  = group?.placeholderValues || {};
        if (!templateDocs.length) {
            notifications.show({ title: t('common.error'), message: t('documents.fileNotAvailable'), color: 'red' });
            return;
        }
        notifications.show({ id: notifId, title: 'শেয়ারের জন্য তৈরি হচ্ছে...', color: 'blue', loading: true, autoClose: false });
        try {
            const fileResults = await Promise.all(
                (group.generatedDocs || []).map(async (genDoc, i) => {
                    const templateDoc = templateDocs[i] ?? templateDocs[0];
                    if (!templateDoc?.contentKey) return null;
                    const blob = await generateDocPdf(templateDoc, placeholders, fieldValues);
                    return new File([blob], `${genDoc.title || group.title || 'document'}.pdf`, { type: 'application/pdf' });
                })
            );
            const files = fileResults.filter(Boolean);
            notifications.hide(notifId);
            if (!files.length) {
                notifications.show({ title: t('common.error'), message: t('documents.fileNotAvailable'), color: 'red' });
                return;
            }
            const used = await shareBlobs(files, group.title || '');
            if (!used) notifications.show({ message: 'PDF ডাউনলোড হচ্ছে।', color: 'blue', autoClose: 2000 });
        } catch (err) {
            notifications.hide(notifId);
            if (err.name !== 'AbortError') notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        }
    };

    // Share the ACTUAL image, never its R2 URL: 'copy' puts the image itself on
    // the clipboard so the user can paste it into WhatsApp / email; other
    // targets go through shareImageToTarget (native sheet with the file
    // attached on mobile, clipboard fallback on desktop).
    const handleShareImage = async (img, type) => {
        const notifId = `img-share-${img.id}`;
        notifications.show({ id: notifId, message: t('imageGeneration.preparingShare'), color: 'blue', loading: true, autoClose: false });
        try {
            // Transparent-subject images (AI cut-outs AND manual edits) are stored without
            // their background — composite the stored colour so copy/share matches the preview.
            const getBlob = async () => {
                const raw = await imageGenerationService.downloadImage(img.id);
                return hasTransparentSubject(img) ? compositeBackground(raw, img.background) : raw;
            };
            let result = 'copied';
            if (type === 'copy') {
                await copyImageToClipboard(getBlob());
            } else {
                result = await shareImageToTarget(getBlob, type, {
                    filename: `${img.fileName || 'sohozkaj-image'}.jpg`,
                    title: t('imageGeneration.shareTitle'),
                });
            }
            notifications.hide(notifId);
            markPrivateResultDelivered(img.id);
            if (result === 'copied') {
                notifications.show({ title: t('imageGeneration.imageCopied'), message: t('imageGeneration.imageCopiedPasteHint'), color: 'green', autoClose: 7000 });
            }
        } catch (error) {
            notifications.hide(notifId);
            if (error?.name === 'AbortError') return; // user closed the native share sheet
            console.error('Share error:', error);
            notifications.show({ title: t('common.error'), message: t('documents.shareFailed'), color: 'red' });
        }
    };

    const handleImagePreviewClick = (image) => {
        setSelectedPreviewImage(image);
        setShowImagePreviewModal(true);
    };

    const handleImageDeleteClick = (img) => {
        setImageToDelete(img);
        setShowImageDeleteConfirm(true);
    };

    const handleImageDeleteConfirm = async () => {
        if (!imageToDelete) return;
        setDeletingImageId(imageToDelete.id);
        try {
            await imageGenerationService.deleteImage(imageToDelete.id);
            notifications.show({ title: t('common.success'), message: t('imageGeneration.imageDeleted') || 'ছবি মুছে ফেলা হয়েছে', color: 'green' });
            setShowImageDeleteConfirm(false);
            // Remove from the accumulated list in place — keeps scroll position (infinite scroll).
            setGeneratedImages(prev => prev.filter(img => img.id !== imageToDelete.id));
            setImageTotal(prev => Math.max(0, prev - 1));
            setImageToDelete(null);
        } catch (error) {
            notifications.show({ title: t('common.error'), message: t('imageGeneration.deleteFailed') || 'ছবি মুছতে ব্যর্থ', color: 'red' });
        } finally {
            setDeletingImageId(null);
        }
    };

    const confirmDelete = async () => {
        if (!groupToDelete) return;
        try {
            setLoading(true);
            const response = await generatedDocService.deleteDocSet(groupToDelete.id);
            if (response.success) {
                notifications.show({ title: t('common.success'), message: t('documents.groupDeleted'), color: 'green' });
                loadDocuments();
            }
        } catch (error) {
            console.error('Delete error:', error);
            notifications.show({ title: t('common.error'), message: t('documents.failedToDelete'), color: 'red' });
        } finally {
            setLoading(false);
            setDeleteModalOpened(false);
            setGroupToDelete(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // ── Tab bar classes — matches the profile-page TabBar (rounded-[11px] segmented
    // buttons; active = solid dark, inactive = white with border).
    const tabCls = (tab) => `flex items-center gap-2 px-[15px] py-[9px] rounded-[11px] text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
        activeTab === tab
            ? 'font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900'
            : 'font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
    }`;

    // ── Skeleton blocks
    const DocSkeleton = () => (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-3 md:p-4">
            <div className="flex flex-col gap-2">
                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded" style={{ height: 14, width: '60%' }} />
                <div className="flex gap-1">
                    <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-full" style={{ height: 18, width: 60 }} />
                    <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-full" style={{ height: 18, width: 50 }} />
                </div>
                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded" style={{ height: 10, width: '40%' }} />
            </div>
        </div>
    );

    return (
        <div className="w-full py-2 md:py-8 font-hind">
            {/* Title + main tabs share one row (tabs right-aligned); sub-tabs render below. */}
            <div className={`flex items-center justify-between gap-4 flex-wrap ${isMobile ? 'mb-3' : 'mb-5'}`}>
                <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                    {t('documents.myGeneratedDocuments')}
                </h1>
                {/* Tab bar — pill buttons on all breakpoints */}
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-nowrap">
                    {[
                        { key: 'images',     icon: <IconPhoto size={isMobile ? 14 : 16} className="text-primary" />, label: t('imageGeneration.myImages') || 'আমার ছবিসমূহ' },
                        { key: 'printMedia', icon: <ModuleGlyph src="/module-icons/print-media.svg" className={isMobile ? '!w-3.5 !h-3.5' : '!w-4 !h-4'} />, label: t('navigation.printMedia', { defaultValue: 'Print Media' }) },
                        { key: 'documents',  icon: <IconFileText size={isMobile ? 14 : 16} className={MY_DOCS_ICON_COLOR} />, label: t('documents.regularDocuments', { defaultValue: 'Documents' }) },
                        { key: 'questions',  icon: <ModuleGlyph src="/questions-create.svg" className={isMobile ? '!w-3.5 !h-3.5' : '!w-4 !h-4'} />, label: t('navigation.questionPapers', { defaultValue: 'Question Papers' }) },
                    ].filter(({ key }) => isBdUser || key !== 'questions').map(({ key, icon, label }) => (
                        <button key={key} className={tabCls(key)} onClick={() => handleTabChange(key)}>
                            {icon}{label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── DOCUMENTS TAB ────────────────────────────────────────── */}
            {activeTab === 'documents' && (
                <div>
                    {/* Sub-tabs: General (template) vs AI document — same pill design as the image sub-tabs */}
                    <div className={`flex items-center gap-2 overflow-x-auto scrollbar-hide flex-nowrap ${isMobile ? 'mb-3' : 'mb-[18px]'}`}>
                        {[
                            { key: 'general', labelKey: 'documents.subGeneral', iconSrc: '/module-icons/document-set.svg' },
                            { key: 'ai',      labelKey: 'documents.subAi',      iconSrc: '/module-icons/ai-documents.svg' },
                        ].filter(({ key }) => isBdUser || key === 'ai').map(({ key, labelKey, iconSrc }) => (
                            <button
                                key={key}
                                onClick={() => changeDocSubTab(key)}
                                className={`flex items-center gap-2 px-[15px] py-[9px] rounded-full text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
                                    docSubTab === key
                                        ? 'font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900'
                                        : 'font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                                }`}
                            >
                                <ModuleGlyph src={iconSrc} className={isMobile ? '!w-3.5 !h-3.5' : '!w-4 !h-4'} />
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>

                    {docSubTab === 'ai' ? (
                        <AiDocumentsList embedded />
                    ) : (
                    <>
                    {/* Search + view toggle */}
                    <div className="flex items-center gap-3 mb-3.5">
                        <div className="flex-1 flex items-center gap-2.5 h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900">
                            <IconSearch size={18} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
                            <input
                                type="text"
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
                                placeholder={t('documents.searchDocuments')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    aria-label={t('common.clear') || 'Clear'}
                                    className="flex-shrink-0 p-0.5 rounded-full text-[#9C988F] dark:text-zinc-400 hover:text-[#1C1B19] dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                                >
                                    <IconX size={16} />
                                </button>
                            )}
                        </div>
                        <div className="flex gap-1 bg-[#F1EFEA] dark:bg-zinc-800 rounded-[10px] p-1 flex-shrink-0">
                            <button
                                onClick={() => { setDocViewMode('list'); localStorage.setItem('docViewMode', 'list'); }}
                                className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${docViewMode === 'list' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                            >
                                <IconList size={17} />
                            </button>
                            <button
                                onClick={() => { setDocViewMode('grid'); localStorage.setItem('docViewMode', 'grid'); }}
                                className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${docViewMode === 'grid' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                            >
                                <IconLayoutGrid size={17} />
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByCategory')}</label>
                            <SelectField
                                value={categoryIdFilter}
                                onChange={setCategoryIdFilter}
                                options={categories}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByLanguage')}</label>
                            <SelectField
                                value={languageFilter}
                                onChange={setLanguageFilter}
                                options={languageOptions}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByTag')}</label>
                            <SelectField
                                value={selectedTags[0] || ''}
                                onChange={(val) => setSelectedTags(val ? [val] : [])}
                                options={[{ value: '', label: t('common.allTags') }, ...availableTags]}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByDate')}</label>
                            <DatePickerField
                                value={dateRange[0]}
                                onChange={(date) => setDateRange(toDayRange(date))}
                                clearable
                                disabled={{ after: new Date() }}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('common.sortBy')}</label>
                            <SelectField
                                value={docSortOrder}
                                onChange={setDocSortOrder}
                                options={[{ value: 'newest', label: t('common.newest') || 'Newest first' }, { value: 'oldest', label: t('common.oldest') || 'Oldest first' }]}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                    </div>

                    {/* Document Groups */}
                    {loading && docSets.length === 0 ? (
                        <div className={docViewMode === 'grid'
                            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
                            : `grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-4'}`
                        }>
                            {Array.from({ length: 6 }).map((_, i) => <DocSkeleton key={i} />)}
                        </div>
                    ) : docSets.length === 0 ? (
                        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-8 text-center">
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('documents.noDocumentsFound')}</p>
                        </div>
                    ) : docViewMode === 'grid' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            {docSets.map((group) => (
                                <div key={group.id} className="group/card bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-2.5 md:p-4 flex flex-col gap-1.5 md:gap-2">
                                    {/* Header row: title + badge + date inline on mobile */}
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100 line-clamp-1 flex-1">{group.title}</p>
                                        <p className="text-[10px] text-zinc-400 dark:text-zinc-500 whitespace-nowrap flex-shrink-0 mt-0.5">{formatDate(group.createdAt)}</p>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {group.template?.category && <Badge size="xs" color="blue">{group.template.category.name}</Badge>}
                                        {group.template?.language && <Badge size="xs" color="green">{t(`templateSets.${group.template.language}`)}</Badge>}
                                        <Badge size="xs">{t('documents.documentsCount', { count: group.generatedDocs?.length || 0 })}</Badge>
                                        {group.generatedDocs?.some(d => d?.metadata?.isManuallyEdited === true) && (
                                            <Badge size="xs" color="orange"><IconPencil size={10} />{t('documents.manuallyEdited')}</Badge>
                                        )}
                                    </div>
                                    {group.template?.tags?.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {group.template.tags.map((tagPivot) => (
                                                <Badge key={tagPivot.tag.id} size="xs" color="grape">{tagPivot.tag.name}</Badge>
                                            ))}
                                        </div>
                                    )}
                                    {/* Docs list */}
                                    <div className="flex flex-col gap-1 mt-0.5">
                                        {group.generatedDocs?.map((doc) => (
                                            <div key={doc.id} className="bg-whitedark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 px-2.5 py-1.5 flex items-center justify-between gap-2">
                                                <p className="text-xs font-medium text-zinc-800 dark:text-zinc-200 line-clamp-1 flex-1">
                                                    {doc.title?.includes(' - ') ? doc.title.substring(doc.title.indexOf(' - ') + 3) : doc.title}
                                                </p>
                                                <div className="flex gap-1 flex-shrink-0">
                                                    <button onClick={() => handleDownloadPDF(doc, group)} className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 hover:bg-red-200 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded transition-colors">
                                                        <IconDownload size={11} />PDF
                                                    </button>
                                                    <button onClick={() => handlePrintDoc(doc, group)} className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded transition-colors">
                                                        <IconPrinter size={11} />{t('documents.print')}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2 mt-auto pt-1 opacity-0 group-hover/card:opacity-100 transition-opacity duration-150">
                                        <button onClick={() => handleRegenerateGroup(group)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-md transition-colors">
                                            <IconRepeat size={14} />{t('documents.regenerateDoc')}
                                        </button>
                                        <button onClick={() => handleShareDocGroup(group)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-green-100 hover:bg-green-200 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-md transition-colors">
                                            <IconShare size={14} />{t('documents.shareDoc')}
                                        </button>
                                        <button onClick={() => handleDeleteGroup(group)} className="p-1.5 rounded-md text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                                            <IconTrash size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={`grid mb-4 ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-[14px]'}`}>
                            {docSets.map((group) => (
                                <div key={group.id} className="bg-white dark:bg-zinc-900 border border-[#ECE9E3] dark:border-zinc-700 rounded-[13px] hover:border-[#E2702E]/40 transition-colors">
                                    <div className="flex items-center gap-[13px] px-[18px] py-4">
                                        <button
                                            onClick={() => toggleGroup(group.id)}
                                            className="flex-shrink-0 text-[#C3BEB4] dark:text-zinc-500 hover:text-[#E2702E] transition-colors"
                                            aria-label={t('documents.toggleDocumentsList')}
                                        >
                                            {expandedGroups.has(group.id) ? <IconCaretDown size={16} /> : <IconCaretRight size={16} />}
                                        </button>
                                        <div className="w-10 h-10 rounded-[10px] bg-[#FCEFE7] dark:bg-orange-950/30 text-[#E2702E] flex items-center justify-center flex-shrink-0">
                                            <IconFileText size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleGroup(group.id)}>
                                            <div className="text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 truncate">{group.title}</div>
                                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                <span className="inline-flex items-center text-[11px] font-semibold text-[#B5862B] dark:text-amber-400 bg-[#F7F0E0] dark:bg-amber-950/30 px-2 py-0.5 rounded-[5px] whitespace-nowrap">
                                                    {t('documents.documentsCount', { count: group.generatedDocs?.length || 0 })}
                                                </span>
                                                {group.generatedDocs?.some(d => d?.metadata?.isManuallyEdited === true) && (
                                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2F7D5B] dark:text-emerald-400 bg-[#EAF3EE] dark:bg-emerald-950/30 px-2 py-0.5 rounded-[5px] whitespace-nowrap">
                                                        <IconPencil size={11} />{t('documents.manuallyEdited')}
                                                    </span>
                                                )}
                                                <span className="text-[11.5px] text-[#A8A294] dark:text-zinc-400 whitespace-nowrap">{formatDate(group.createdAt)}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-[7px] flex-shrink-0">
                                            <TipBtn
                                                onClick={() => handleRegenerateGroup(group)}
                                                tip={t('documents.regenerateDocuments')}
                                                className="w-8 h-8 border border-[#ECE9E3] dark:border-zinc-700 rounded-lg flex items-center justify-center text-[#6E6A63] dark:text-zinc-300 hover:text-[#E2702E] hover:border-[#E2702E]/40 transition-colors"
                                            >
                                                <IconPencil size={15} />
                                            </TipBtn>
                                            <DropdownMenu
                                                trigger={
                                                    <button className="w-8 h-8 border border-[#ECE9E3] dark:border-zinc-700 rounded-lg flex items-center justify-center text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100 transition-colors">
                                                        <IconDots size={15} />
                                                    </button>
                                                }
                                                items={[
                                                    { icon: <IconShare size={14} className="text-green-600" />, text: t('documents.shareDoc'), onClick: () => handleShareDocGroup(group) },
                                                    { icon: <IconTrash size={14} className="text-red-600" />, text: t('common.delete'), onClick: () => handleDeleteGroup(group) },
                                                ]}
                                            />
                                        </div>
                                    </div>

                                    {/* Expanded Documents */}
                                    {expandedGroups.has(group.id) && (
                                        <div className="flex flex-col gap-1.5 px-[18px] pb-4">
                                            {group.generatedDocs?.map((doc) => (
                                                <div key={doc.id} className="bg-[#FAF9F6] dark:bg-zinc-800 hover:bg-[#F4F2EE] dark:hover:bg-zinc-800 rounded-xl border border-[#ECE9E3] dark:border-zinc-700 px-3 py-2 transition-colors">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-medium text-[#1C1B19] dark:text-zinc-100 truncate">
                                                                {doc.title?.includes(' - ') ? doc.title.substring(doc.title.indexOf(' - ') + 3) : doc.title}
                                                            </p>
                                                            <p className="text-[10px] text-[#A8A294] dark:text-zinc-400">{formatDate(doc.updatedAt)}</p>
                                                        </div>
                                                        <div className="flex gap-1.5 flex-shrink-0">
                                                            <TipBtn onClick={() => handleDownloadPDF(doc, group)} className="inline-flex items-center gap-0.5 px-2 py-1 text-[10px] font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30 hover:bg-[#fbe6d9] dark:hover:bg-orange-950/30 rounded-md transition-colors" tip={t('documents.downloadAsPdf')}>
                                                                <IconDownload size={11} />PDF
                                                            </TipBtn>
                                                            <TipBtn onClick={() => handlePrintDoc(doc, group)} className="inline-flex items-center gap-0.5 px-2 py-1 text-[10px] font-semibold text-[#6E6A63] dark:text-zinc-300 bg-[#F1EFEA] dark:bg-zinc-800 hover:bg-[#e9e6e0] dark:hover:bg-zinc-800 rounded-md transition-colors" tip={t('documents.docPrint')}>
                                                                <IconPrinter size={11} />{t('documents.print')}
                                                            </TipBtn>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    <PaginationBar
                        page={pagination.page}
                        totalPages={pagination.totalPages}
                        total={pagination.total}
                        pageSize={pagination.limit}
                        currentCount={docSets.length}
                        onPage={goToDocPage}
                        className="mt-[26px]"
                    />
                    </>
                    )}
                </div>
            )}

            {/* ── IMAGES TAB ───────────────────────────────────────────── */}
            {activeTab === 'images' && (
                <div>
                    {/* Image sub-tabs — same pill design as the main tab bar, slightly smaller */}
                    <div className={`flex items-center gap-2 overflow-x-auto scrollbar-hide flex-nowrap ${isMobile ? 'mb-3' : 'mb-[18px]'}`}>
                        {[
                            { key: 'all',        labelKey: 'documents.allImages',           iconSrc: '/module-icons/ai-tools.svg' },
                            { key: 'manual',     labelKey: 'documents.manuallyEditedImages', iconSrc: '/module-icons/manual-photo-edit.svg' },
                            { key: 'aiTemplate', labelKey: 'documents.aiTemplateImages',     iconSrc: '/module-icons/ai-templates.svg' },
                            { key: 'studio',     labelKey: 'documents.studioImages',         iconSrc: '/module-icons/studio-editor.svg' },
                            { key: 'bulk',       labelKey: 'documents.bulkPhotoEditImages',  iconSrc: '/module-icons/bulk-photo-editing.svg' },
                        ].map(({ key, labelKey, iconSrc }) => (
                            <button
                                key={key}
                                onClick={() => changeImageSubTab(key)}
                                className={`flex items-center gap-2 px-[15px] py-[9px] rounded-full text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
                                    imageSubTab === key
                                        ? 'font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900'
                                        : 'font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                                }`}
                            >
                                <ModuleGlyph src={iconSrc} className={isMobile ? '!w-3.5 !h-3.5' : '!w-4 !h-4'} />
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>

                    {/* The Bulk Photo Edit tab reuses the project list page (embedded);
                        every other sub-tab shows the standard image feed below. */}
                    {imageSubTab === 'bulk' ? (
                        <BulkPhotoEditList embedded />
                    ) : (
                    <>
                    {/* Search + filters + view toggle */}
                    <div className="flex items-center gap-3 mb-5 flex-wrap">
                        <div className="flex-1 min-w-[200px] flex items-center gap-2.5 h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900">
                            <IconSearch size={18} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
                            <input
                                type="text"
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
                                placeholder={t('imageGeneration.searchImages') || 'Search images'}
                                value={imageSearch}
                                onChange={(e) => setImageSearch(e.target.value)}
                            />
                            {imageSearch && (
                                <button
                                    type="button"
                                    onClick={() => setImageSearch('')}
                                    aria-label={t('common.clear') || 'Clear'}
                                    className="flex-shrink-0 p-0.5 rounded-full text-[#9C988F] dark:text-zinc-400 hover:text-[#1C1B19] dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                                >
                                    <IconX size={16} />
                                </button>
                            )}
                        </div>
                        <div className="w-full sm:w-[180px]">
                            <DatePickerField
                                value={imageDateRange[0]}
                                onChange={(date) => setImageDateRange(toDayRange(date))}
                                clearable
                                disabled={{ after: new Date() }}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        {(imageSubTab === 'all' || imageSubTab === 'manual' || imageSubTab === 'studio') && (
                            <>
                                <div className="w-full sm:w-[170px]">
                                    <SelectField
                                        value={imageBgFilter}
                                        onChange={setImageBgFilter}
                                        clearable
                                        options={[
                                            { value: '', label: t('imageFilters.allBackgrounds') },
                                            // Only actual COLOURS the user has — hex, rgb, transparent, or a known
                                            // named colour id (manual images store 'blue'/'sky_blue'… not hex).
                                            // Scene/preset tokens (stadium, preset:*, no_change…) and junk (var(--…))
                                            // are excluded so every row shows a real, visible colour swatch.
                                            ...imageFilterOptions.backgrounds
                                              .filter((tok) => tok === 'transparent' || String(tok).startsWith('#') || String(tok).startsWith('rgb') || BG_COLOR_KEY[tok])
                                              .map((tok) => {
                                                const c = bgSwatchColor(tok);
                                                const name = tok === 'transparent'
                                                    ? t('imageFilters.transparent')
                                                    : BG_COLOR_KEY[tok] ? t(`imageFilters.color_${BG_COLOR_KEY[tok]}`)
                                                    : tok;
                                                return {
                                                    value: tok,
                                                    label: (
                                                        <span className="flex items-center gap-2 min-w-0">
                                                            <span
                                                                className="w-3.5 h-3.5 rounded-[4px] border border-zinc-300 dark:border-zinc-600 flex-shrink-0"
                                                                style={c ? { background: c } : { background: 'repeating-conic-gradient(#cbd5e1 0% 25%,#fff 0% 50%) 0 0/6px 6px' }}
                                                            />
                                                            <span className="truncate">{name}</span>
                                                        </span>
                                                    ),
                                                };
                                            }),
                                        ]}
                                        triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                                    />
                                </div>
                                {(imageSubTab === 'all' || imageSubTab === 'studio') && (
                                    <div className="w-full sm:w-[170px]">
                                        <SelectField
                                            value={imageDressFilter}
                                            onChange={setImageDressFilter}
                                            clearable
                                            options={[
                                                { value: '', label: t('imageFilters.allDress') },
                                                ...imageFilterOptions.dressCodes.map((k) => ({ value: k, label: translateDress(k, i18n.language === 'bn') })),
                                            ]}
                                            triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {imageSubTab === 'aiTemplate' && (
                            <div className="w-full sm:w-[190px]">
                                <SelectField
                                    value={imageCategoryFilter}
                                    onChange={setImageCategoryFilter}
                                    clearable
                                    options={[
                                        { value: '', label: t('imageFilters.allCategories') },
                                        ...imageFilterOptions.categories.map((c) => ({
                                            value: String(c.id),
                                            label: (i18n.language || '').startsWith('bn') ? (c.nameBn || c.name) : c.name,
                                        })),
                                    ]}
                                    triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                                />
                            </div>
                        )}
                        <div className="w-full sm:w-[170px]">
                            <SelectField
                                value={imageSortOrder}
                                onChange={setImageSortOrder}
                                options={[{ value: 'newest', label: t('common.newest') || 'Newest first' }, { value: 'oldest', label: t('common.oldest') || 'Oldest first' }]}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        <div className="flex gap-1 bg-[#F1EFEA] dark:bg-zinc-800 rounded-[10px] p-1 flex-shrink-0">
                            <button
                                onClick={() => { setImageViewMode('grid'); localStorage.setItem('imageViewMode', 'grid'); }}
                                className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${imageViewMode === 'grid' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                            >
                                <IconLayoutGrid size={17} />
                            </button>
                            <button
                                onClick={() => { setImageViewMode('list'); localStorage.setItem('imageViewMode', 'list'); }}
                                className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${imageViewMode === 'list' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}
                            >
                                <IconList size={17} />
                            </button>
                        </div>
                        {hasActiveImageFilters && (
                            <button
                                type="button"
                                onClick={resetImageFilters}
                                className="h-11 px-3.5 inline-flex items-center gap-1.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13.5px] font-medium text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
                            >
                                <IconRefresh size={15} /> {t('common.reset')}
                            </button>
                        )}
                    </div>

                    {imagesLoading && generatedImages.length === 0 ? (
                        imageViewMode === 'grid' ? (
                            /* ── Masonry skeleton — mirrors the real Pinterest-style grid ── */
                            <Masonry
                                breakpointCols={imageMasonryBreakpoints}
                                className="flex w-auto -ml-[14px] mb-8"
                                columnClassName="pl-[14px] bg-clip-padding"
                            >
                                {Array.from({ length: 20 }).map((_, i) => (
                                    <div key={i} className="mb-[14px] rounded-[13px] border border-[#ECE9E3] dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900">
                                        <div
                                            className="w-full animate-pulse bg-[#F0EFEC] dark:bg-zinc-800"
                                            style={{ height: IMAGE_SKELETON_HEIGHTS[i % IMAGE_SKELETON_HEIGHTS.length] }}
                                        />
                                        <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="h-2.5 w-3/4 rounded bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse mb-1.5" />
                                                <div className="h-2 w-2/5 rounded bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse" />
                                            </div>
                                            <div className="w-[26px] h-[26px] rounded-[7px] bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse flex-shrink-0" />
                                        </div>
                                    </div>
                                ))}
                            </Masonry>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div key={i} className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-3">
                                        <div className="flex gap-3 items-center">
                                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-md flex-shrink-0" style={{ height: 56, width: 56 }} />
                                            <div className="flex-1 flex flex-col gap-1.5">
                                                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded" style={{ height: 10 }} />
                                                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded" style={{ height: 10, width: '40%' }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : !imagesLoading && displayedImages.length === 0 ? (
                        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-8">
                            <div className="flex flex-col items-center gap-3">
                                <IconPhoto size={48} className="text-zinc-400" />
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('imageGeneration.noImages') || 'কোন ছবি তৈরি করা হয়নি'}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {imageViewMode === 'grid' ? (
                                /* ── Masonry grid (uniform width, height follows the image) ── */
                                <Masonry
                                    breakpointCols={imageMasonryBreakpoints}
                                    className="flex w-auto -ml-[14px] mb-8"
                                    columnClassName="pl-[14px] bg-clip-padding"
                                >
                                    {pagedImages.map((img) => (
                                        <div
                                            key={img.id}
                                            className="group/img mb-[14px] border border-[#ECE9E3] dark:border-zinc-700 rounded-[13px] overflow-hidden bg-white dark:bg-zinc-900 hover:border-[#E2702E]/40 hover:shadow-sm transition-all"
                                        >
                                            {/* Image — height reserved via aspect-ratio so the masonry
                                                doesn't collapse before the (lazy) image downloads; the
                                                real ratio is applied on load. */}
                                            <div
                                                className="relative w-full overflow-hidden cursor-pointer bg-[#F0EFEC] dark:bg-zinc-800"
                                                style={{ aspectRatio: '3 / 4' }}
                                                onClick={() => handleImagePreviewClick(img)}
                                            >
                                                <img
                                                    src={img.aiImage}
                                                    alt="Generated"
                                                    loading="lazy"
                                                    onLoad={(e) => {
                                                        const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                                                        if (w && h && e.currentTarget.parentElement) {
                                                            e.currentTarget.parentElement.style.aspectRatio = `${w} / ${h}`;
                                                        }
                                                    }}
                                                    className="w-full h-full object-cover block"
                                                    style={hasTransparentSubject(img) ? manualBgStyle(img.background) : undefined}
                                                />
                                                {/* HD / Ultra HD photos carry a floating badge */}
                                                <QualityBadge quality={img.quality} className="absolute left-2 top-2" />
                                                {/* Quick print — hover shortcut so the ⋯ menu isn't needed */}
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); setPrintImageUrl(img.aiImage); setCustomDownloadImageId(img.id); setShowPrintModal(true); }}
                                                            className="absolute top-2 right-2 p-2 rounded-lg bg-white/90 dark:bg-zinc-900/90 text-zinc-700 dark:text-zinc-200 shadow-sm opacity-0 group-hover/img:opacity-100 hover:bg-primary hover:text-white transition-all"
                                                        >
                                                            <IconPrinter size={16} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('imageGeneration.printImage')}</TooltipContent>
                                                </Tooltip>
                                            </div>

                                            {/* Footer: name + date + actions menu */}
                                            <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                                                <div className="min-w-0 flex-1">
                                                    {editingImageId === img.id ? (
                                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="text"
                                                                className="flex-1 min-w-0 px-1.5 py-0.5 text-xs rounded border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#1C1B19] dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-[#E2702E]"
                                                                value={editingImageName}
                                                                onChange={(e) => setEditingImageName(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveImageName(img.id);
                                                                    if (e.key === 'Escape') { setEditingImageId(null); setEditingImageName(''); }
                                                                }}
                                                                autoFocus
                                                            />
                                                            <button onClick={() => handleSaveImageName(img.id)} className="p-0.5 rounded bg-green-500 text-white flex-shrink-0"><IconCheck size={11} /></button>
                                                            <button onClick={() => { setEditingImageId(null); setEditingImageName(''); }} className="p-0.5 rounded bg-red-500 text-white flex-shrink-0"><IconX size={11} /></button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div
                                                                className="text-[12.5px] font-semibold text-[#1C1B19] dark:text-zinc-100 truncate cursor-text select-none"
                                                                title={img.fileName || 'Untitled'}
                                                                onDoubleClick={(e) => { e.stopPropagation(); setEditingImageId(img.id); setEditingImageName(img.fileName || ''); }}
                                                            >
                                                                {img.fileName || t('imageGeneration.untitled') || 'Untitled'}
                                                            </div>
                                                            {img.createdAt && (
                                                                <div className="text-[10.5px] text-[#A8A294] dark:text-zinc-400 mt-0.5">{new Date(img.createdAt).toLocaleDateString()}</div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                <DropdownMenu
                                                    trigger={
                                                        // Button bg is a fixed light amber (no dark variant), so the icon must
                                                        // stay DARK in dark mode too — otherwise the dots vanish on the amber.
                                                        <button className="w-[26px] h-[26px] border border-[#ECE9E3] dark:border-amber-900/40 rounded-[7px] flex items-center justify-center text-[#6E6A63] hover:text-[#1C1B19] dark:text-[#4a3f22] dark:hover:text-black transition-colors flex-shrink-0 bg-[#f5daa3]">
                                                            <IconDots size={15} />
                                                        </button>
                                                    }
                                                    items={[
                                                        { icon: <IconEye size={14} />, text: t('imageGeneration.previewTooltip'), onClick: () => handleImagePreviewClick(img) },
                                                        { label: t('imageGeneration.downloadOptions') },
                                                        { icon: <IconDownload size={14} />, text: t('imageGeneration.downloadOriginal'), onClick: () => handleDownloadImage(img) },
                                                        { icon: <IconPlus size={14} />, text: t('imageGeneration.customSizeQuality'), onClick: () => { setCustomDownloadImageUrl(img.aiImage); setCustomDownloadImageId(img.id); setCustomDownloadImageBg(hasTransparentSubject(img) ? img.background : null); setShowCustomDownload(true); } },
                                                        { icon: <IconSparkles size={14} />, text: t('imageGeneration.tooltipReopenEditor'), onClick: () => handleReopenImage(img) },
                                                        { icon: <IconPrinter size={14} />, text: t('imageGeneration.printImage'), onClick: () => { setPrintImageUrl(img.aiImage); setCustomDownloadImageId(img.id); setShowPrintModal(true); } },
                                                        { icon: <IconCopy size={14} />, text: t('imageGeneration.copyImage'), onClick: () => handleShareImage(img, 'copy') },
                                                        { icon: <IconPencil size={14} />, text: t('common.rename') || 'Rename', onClick: () => { setEditingImageId(img.id); setEditingImageName(img.fileName || ''); } },
                                                        { icon: <IconTrash size={14} className="text-red-600" />, text: t('common.delete') || 'Delete', onClick: () => handleImageDeleteClick(img) },
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </Masonry>
                            ) : (
                                <div className={`grid gap-2 mb-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                    {pagedImages.map((img) => (
                                        <div
                                            key={img.id}
                                            className="group/imgrow relative flex items-stretch bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden transition-all duration-200 hover:shadow-md"
                                            style={{ borderColor: hoveredImageId === img.id ? '#e68a00' : undefined }}
                                            onMouseEnter={() => setHoveredImageId(img.id)}
                                            onMouseLeave={() => setHoveredImageId(null)}
                                        >
                                            {/* Orange left accent bar */}
                                            <div className="w-1 flex-shrink-0 bg-zinc-100 dark:bg-zinc-800 group-hover/imgrow:bg-primary transition-colors duration-200" />

                                            {/* Thumbnail */}
                                            <div className="flex-shrink-0 p-2">
                                                <div
                                                    className="relative rounded-lg overflow-hidden shadow-sm cursor-pointer flex-shrink-0"
                                                    style={{ width: 62, height: 76, ...(hasTransparentSubject(img) ? manualBgStyle(img.background) : {}) }}
                                                    onClick={() => handleImagePreviewClick(img)}
                                                >
                                                    <img
                                                        src={img.aiImage}
                                                        alt="Generated"
                                                        className="w-full h-full object-contain"
                                                    />
                                                    <QualityBadge quality={img.quality} size="sm" className="absolute bottom-1 left-1" />
                                                </div>
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0 flex flex-col justify-center py-2 pr-2">
                                                <div className="flex items-center gap-1 min-w-0">
                                                    {editingImageId === img.id ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                className="flex-1 min-w-0 text-sm font-semibold text-zinc-800 dark:text-zinc-100 bg-transparent border-b border-primary focus:outline-none"
                                                                value={editingImageName}
                                                                onChange={(e) => setEditingImageName(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveImageName(img.id);
                                                                    if (e.key === 'Escape') { setEditingImageId(null); setEditingImageName(''); }
                                                                }}
                                                                autoFocus
                                                            />
                                                            <button onClick={() => handleSaveImageName(img.id)} className="p-0.5 rounded-full bg-green-500 text-white flex-shrink-0"><IconCheck size={11} /></button>
                                                            <button onClick={() => { setEditingImageId(null); setEditingImageName(''); }} className="p-0.5 rounded-full bg-red-500 text-white flex-shrink-0"><IconX size={11} /></button>
                                                        </>
                                                    ) : (
                                                        <p
                                                            className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 line-clamp-1 cursor-text select-none"
                                                            title={img.fileName || 'Untitled'}
                                                            onDoubleClick={(e) => { e.stopPropagation(); setEditingImageId(img.id); setEditingImageName(img.fileName || ''); }}
                                                        >
                                                            {img.fileName || t('imageGeneration.untitled') || 'Untitled'}
                                                        </p>
                                                    )}
                                                </div>
                                                {img.createdAt && (
                                                    <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                                                        {new Date(img.createdAt).toLocaleDateString()}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Action buttons — appear on hover */}
                                            <div className="flex items-center gap-1 pr-2 flex-shrink-0 opacity-0 group-hover/imgrow:opacity-100 transition-opacity duration-150">
                                                <DropdownMenu
                                                    trigger={<button className="p-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-blue-100 hover:text-blue-600 dark:hover:bg-blue-900/40 dark:hover:text-blue-400 transition-colors"><IconDownload size={14} /></button>}
                                                    items={[
                                                        { label: t('imageGeneration.downloadOptions') },
                                                        { icon: <IconDownload size={14} />, text: t('imageGeneration.downloadOriginal'), onClick: () => handleDownloadImage(img) },
                                                        { icon: <IconPlus size={14} />, text: t('imageGeneration.customSizeQuality'), onClick: () => { setCustomDownloadImageUrl(img.aiImage); setCustomDownloadImageId(img.id); setCustomDownloadImageBg(hasTransparentSubject(img) ? img.background : null); setShowCustomDownload(true); } },
                                                    ]}
                                                />
                                                <button title={t('imageGeneration.previewTooltip')} onClick={() => handleImagePreviewClick(img)} className="p-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-blue-100 hover:text-blue-600 dark:hover:bg-blue-900/40 dark:hover:text-blue-400 transition-colors"><IconEye size={14} /></button>
                                                <button onClick={() => handleReopenImage(img)} className="p-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition-colors" title={t('imageGeneration.tooltipReopenEditor')}><IconSparkles size={14} /></button>
                                                <button title={t('imageGeneration.printImage')} onClick={() => { setPrintImageUrl(img.aiImage); setCustomDownloadImageId(img.id); setShowPrintModal(true); }} className="p-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-cyan-100 hover:text-cyan-600 dark:hover:bg-cyan-900/40 dark:hover:text-cyan-400 transition-colors"><IconPrinter size={14} /></button>
                                                {/* Copy — direct action (the share dropdown is gone, same as the grid menu) */}
                                                <button title={t('imageGeneration.copyImage')} onClick={() => handleShareImage(img, 'copy')} className="p-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-violet-100 hover:text-violet-600 dark:hover:bg-violet-900/40 dark:hover:text-violet-400 transition-colors"><IconCopy size={14} /></button>
                                                <button title={t('common.rename') || 'Rename'} onClick={() => { setEditingImageId(img.id); setEditingImageName(img.fileName || ''); }} className="p-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-emerald-100 hover:text-emerald-600 dark:hover:bg-emerald-900/40 dark:hover:text-emerald-400 transition-colors"><IconPencil size={14} /></button>
                                                <button title={t('common.delete') || 'Delete'} disabled={deletingImageId === img.id} onClick={() => handleImageDeleteClick(img)} className="p-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/40 dark:hover:text-red-400 transition-colors disabled:opacity-50"><IconTrash size={14} /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Infinite scroll: this sentinel auto-loads the next page as it
                                nears the viewport (see the IntersectionObserver effect). */}
                            {imageHasMore && (
                                <div ref={loadMoreRef} className="flex justify-center py-8">
                                    {imageLoadingMore && (
                                        <span className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    </>
                    )}
                </div>
            )}

            {/* ── PRINT MEDIA TAB (under reconstruction) ─────────────────── */}
            {activeTab === 'printMedia' && (
                <PrintMediaTab
                    officeSpaceId={selectedOfficeSpace?.id}
                    onOpenEditor={() => navigate('/print-media')}
                />
            )}

            {activeTab === 'questions' && (
                <QuestionsTab
                    officeSpaceId={selectedOfficeSpace?.id}
                    onCreate={() => navigate('/questions-create/create')}
                />
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmDialog
                open={deleteModalOpened}
                onClose={() => setDeleteModalOpened(false)}
                onConfirm={confirmDelete}
                title={t('documents.deleteDocumentGroup')}
                description={t('documents.confirmDelete', { title: groupToDelete?.title })}
                confirmLabel={t('common.delete')}
                loading={loading}
            />

            {/* Regenerate Document Drawer */}
            {regenerateDrawerOpened && (
                <GenerateDocDrawer
                    opened={regenerateDrawerOpened}
                    onClose={handleDrawerClose}
                    documentGroup={groupToRegenerate}
                    onSuccess={handleRegenerateSuccess}
                />
            )}

            {/* Custom Download Modal for Images */}
            <CustomDownloadModal
                opened={showCustomDownload}
                onClose={() => setShowCustomDownload(false)}
                imageUrl={customDownloadImageUrl}
                imageId={customDownloadImageId}
                bgKey={customDownloadImageBg}
            />

            {/* Print Modal for Images */}
            <PrintModal
                opened={showPrintModal}
                onClose={() => setShowPrintModal(false)}
                imageUrl={printImageUrl}
                imageId={customDownloadImageId}
            />

            {/* Image Preview Modal with Slider */}
            {/* onImageDeleted(imageId): drop it from the accumulated list in place so the
                infinite-scroll position and other loaded pages are preserved. */}
            <ImagePreviewModal
                opened={showImagePreviewModal}
                onClose={() => setShowImagePreviewModal(false)}
                images={activeTab === 'images' ? generatedImages : generatedImages}
                initialImage={selectedPreviewImage}
                onImageDeleted={(deletedId) => {
                    setGeneratedImages(prev => prev.filter(i => i.id !== deletedId));
                    setImageTotal(prev => Math.max(0, prev - 1));
                }}
                onPrint={(imageUrl, imageId) => {
                    setPrintImageUrl(imageUrl);
                    setCustomDownloadImageId(imageId);
                    setShowPrintModal(true);
                    setShowImagePreviewModal(false);
                }}
                onCustomDownload={(imageUrl, imageId) => {
                    const f = generatedImages.find(i => i.id === imageId);
                    setCustomDownloadImageUrl(imageUrl);
                    setCustomDownloadImageId(imageId);
                    setCustomDownloadImageBg(f?.manuallyEdited ? f?.background : null);
                    setShowCustomDownload(true);
                }}
            />

            {/* Image Delete Confirmation Modal */}
            <ConfirmDialog
                open={showImageDeleteConfirm}
                onClose={() => { setShowImageDeleteConfirm(false); setImageToDelete(null); }}
                onConfirm={handleImageDeleteConfirm}
                title={t('imageGeneration.deleteImage')}
                description={t('imageGeneration.deleteConfirmation')}
                confirmLabel={t('common.delete')}
                loading={!!deletingImageId}
            />
        </div>
    );
}
